/**
 * Immediate-window *view* — the hybrid terminal a kid actually types into.
 *
 * "Hybrid" = terminal *feel* (monospace, a `>>>` prompt, a scrolling transcript,
 * ↑/↓ command recall) with kid-friendly guardrails (big text, friendly errors
 * from `errors.ts`, zero shell internals). It is the DOM half of the model/view
 * split: all state lives in the {@link ReplSession}; running lives in
 * `runner.ts`; this file only renders and wires input.
 *
 * Wiring summary:
 *   - Enter runs the current line (unless it looks unfinished — see
 *     {@link needsContinuation} — or Shift is held, which both insert a newline
 *     for multi-line code).
 *   - ↑/↓ recall previous/next commands when the prompt is a single line.
 *   - Every run is also handed to the lesson engine via `submitExercise`, so an
 *     exercise's success check can be satisfied by what the kid runs here.
 *
 * DOM-free under test: a fake `Document` records the tree, element `value`s and
 * keydown listeners, so we can drive the prompt exactly like a kid would.
 */
import type { PythonRuntime } from "../pyodide/types";
import type { ExerciseContext } from "../lessons/content";
import { runCell } from "./runner";
import {
  entryOutputText,
  needsContinuation,
  type ReplEntry,
  type ReplSession,
} from "./model";

/** The slice of the lesson engine the REPL needs (keeps coupling minimal). */
export interface ExerciseSink {
  submitExercise(ctx: ExerciseContext): { passed: boolean; advanced: boolean };
}

export interface ReplViewOptions {
  /** The Python runtime to evaluate submissions against. */
  runtime: PythonRuntime;
  /** The session model holding history + transcript. */
  session: ReplSession;
  /** Optional lesson engine to receive each run's output for success checks. */
  engine?: ExerciseSink;
}

/** Public surface for mounting + programmatically driving the REPL. */
export interface ReplView {
  /** Render (and keep re-rendering) the REPL into `container`. */
  mount(container: HTMLElement): void;
  /** Run a source string as if the kid had typed + entered it. */
  run(source: string): Promise<ReplEntry>;
  /** The current draft text in the prompt (handy for tests). */
  readonly draft: string;
}

/** The `>>>` shown before the first line, and the `...` continuation prompt. */
export const PRIMARY_PROMPT = ">>>";
export const CONTINUATION_PROMPT = "...";

/** A textarea-like element: the extra bits we touch on the live prompt. */
interface InputLike extends HTMLElement {
  value: string;
  setSelectionRange(start: number, end: number): void;
}

/** Create a REPL view bound to a runtime + session (+ optional lesson engine). */
export function createReplView(
  doc: Document,
  options: ReplViewOptions,
): ReplView {
  const { runtime, session, engine } = options;

  let container: HTMLElement | null = null;
  let log: HTMLElement | null = null;
  let input: InputLike | null = null;
  let busy = false; // guards against double-submits while Python is running.
  let celebrate = false; // show a "lesson passed" flag after the last run.
  let unsubscribe: (() => void) | null = null;

  const el = (tag: string, className?: string, text?: string): HTMLElement => {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  /* ------------------------------ rendering ----------------------------- */

  /** Render one transcript entry: echoed input, output, result, or error. */
  function renderEntry(entry: ReplEntry): HTMLElement {
    const root = el("div", "repl__entry");
    root.dataset.ok = String(entry.ok);

    // Echo the submitted source behind a prompt, continuation lines aligned.
    const echo = el("div", "repl__echo");
    const lines = entry.input.split("\n");
    lines.forEach((line, i) => {
      const row = el("div", "repl__echo-line");
      const prompt = el(
        "span",
        "repl__prompt",
        i === 0 ? PRIMARY_PROMPT : CONTINUATION_PROMPT,
      );
      prompt.setAttribute("aria-hidden", "true");
      row.append(prompt, el("span", "repl__echo-text", line));
      echo.append(row);
    });
    root.append(echo);

    // Streamed stdout/stderr, tagged so CSS can colour stderr differently.
    for (const chunk of entry.outputs) {
      const out = el("pre", "repl__out", chunk.text);
      out.dataset.stream = chunk.stream;
      root.append(out);
    }

    // The value of the last expression (REPL-style inline result).
    if (entry.result !== null) {
      root.append(el("pre", "repl__result", entry.result));
    }

    // A friendly, unscary error — with the real traceback tucked away.
    if (entry.error) {
      const box = el("div", "repl__error");
      box.dataset.kind = entry.error.kind;
      box.append(el("p", "repl__error-title", entry.error.title));
      box.append(el("p", "repl__error-detail", entry.error.detail));

      const details = doc.createElement("details");
      details.className = "repl__error-raw";
      const summary = el("summary", "repl__error-summary", "Robot details");
      details.append(summary, el("pre", "repl__error-trace", entry.error.raw));
      box.append(details);
      root.append(box);
    }

    return root;
  }

  /** Rebuild the scrolling transcript from the session's entries. */
  function renderLog(): void {
    if (!log) return;
    const children = session.entries.map(renderEntry);
    if (celebrate) {
      children.push(
        el("p", "repl__lesson-pass", "🎉 You did it — lesson step complete!"),
      );
    }
    log.replaceChildren(...children);
    // Keep the newest output in view (no-op under the fake DOM).
    if (typeof (log as { scrollTop?: number }).scrollTop === "number") {
      (log as { scrollTop: number; scrollHeight: number }).scrollTop = (
        log as { scrollHeight: number }
      ).scrollHeight;
    }
  }

  /* ------------------------------- running ------------------------------ */

  /**
   * Run a source string: evaluate it, record it, feed the output to the lesson
   * engine, then re-render. Returns the entry so tests/tutor can inspect it.
   */
  async function run(source: string): Promise<ReplEntry> {
    busy = true;
    celebrate = false;
    try {
      const entry = await runCell(runtime, source);
      session.record(entry); // → onChange → renderLog

      if (engine) {
        const ctx: ExerciseContext = { output: entryOutputText(entry) };
        const result = engine.submitExercise(ctx);
        celebrate = result.passed;
      }
      renderLog();
      return entry;
    } finally {
      busy = false;
    }
  }

  /* ------------------------------- input -------------------------------- */

  /** Move the caret to the end of the prompt after a history recall. */
  function caretToEnd(): void {
    if (!input) return;
    const end = input.value.length;
    input.setSelectionRange?.(end, end);
  }

  /** Submit whatever is currently in the prompt (if it isn't blank). */
  async function submitCurrent(): Promise<void> {
    if (!input || busy) return;
    const source = input.value;
    if (source.trim() === "") return;
    input.value = "";
    session.resetHistoryCursor();
    await run(source);
    input?.focus?.();
  }

  /** Keyboard contract: Enter runs / continues; ↑/↓ recall history. */
  function handleKey(event: KeyboardEvent): void {
    if (!input) return;

    if (event.key === "Enter") {
      // Shift+Enter, or an obviously-unfinished line, inserts a newline (the
      // browser's default for a textarea) so the kid can write a block.
      if (event.shiftKey || needsContinuation(input.value)) return;
      event.preventDefault();
      void submitCurrent();
      return;
    }

    // History recall only when editing a single line, so multi-line arrow
    // navigation still works inside a block.
    const singleLine = !input.value.includes("\n");

    if (event.key === "ArrowUp" && singleLine) {
      const prev = session.historyPrev(input.value);
      if (prev !== null) {
        event.preventDefault();
        input.value = prev;
        caretToEnd();
      }
      return;
    }

    if (event.key === "ArrowDown" && singleLine) {
      const next = session.historyNext();
      if (next !== null) {
        event.preventDefault();
        input.value = next;
        caretToEnd();
      }
    }
  }

  /* ------------------------------- mount -------------------------------- */

  function build(): HTMLElement {
    const root = el("section", "repl");
    root.dataset.replRoot = "true";
    root.setAttribute("aria-label", "Python immediate window");

    log = el("div", "repl__log");
    log.dataset.slot = "transcript";
    log.setAttribute("role", "log");
    log.setAttribute("aria-live", "polite");
    root.append(log);

    const row = el("div", "repl__inputrow");
    const prompt = el("span", "repl__prompt repl__prompt--live", PRIMARY_PROMPT);
    prompt.setAttribute("aria-hidden", "true");

    const ta = doc.createElement("textarea") as unknown as InputLike;
    ta.className = "repl__input";
    ta.dataset.slot = "input";
    ta.setAttribute("rows", "1");
    ta.setAttribute("spellcheck", "false");
    ta.setAttribute("autocomplete", "off");
    ta.setAttribute("placeholder", 'print("hello")');
    ta.setAttribute("aria-label", "Type Python here, then press Enter to run");
    if (typeof ta.value !== "string") ta.value = "";
    ta.addEventListener("keydown", (ev) => handleKey(ev as KeyboardEvent));
    input = ta;

    row.append(prompt, ta);
    root.append(row);

    const hint = el(
      "p",
      "repl__hint",
      "Type Python and press Enter to run it ▶  (Shift+Enter for a new line)",
    );
    root.append(hint);

    return root;
  }

  return {
    mount(target: HTMLElement) {
      container = target;
      // Idempotent re-mount (the layout recreates the pane on a stage switch):
      // drop the stale subscription before wiring fresh DOM.
      unsubscribe?.();
      const tree = build();
      container.replaceChildren(tree);
      renderLog();
      unsubscribe = session.onChange(renderLog);
      input?.focus?.();
    },
    run,
    get draft() {
      return input?.value ?? "";
    },
  };
}
